import { appSettingsAPI } from '../services/api'
import { t, locale, isRTL, getLanguage } from '../i18n'

// The store's own name, contact details and currency for printed bills.
export async function loadBusinessInfo(fallbackCurrency = 'OMR') {
  const info = { name: '', address: '', phone: '', email: '', logoUrl: '', currency: fallbackCurrency, taxRate: 0, serviceCharge: 0, footer: '' }
  try {
    const { data } = await appSettingsAPI.getSettings()
    const s = data?.settings || {}
    const v = key => (s[key] && typeof s[key] === 'object' && 'value' in s[key] ? s[key].value : s[key]) ?? ''
    Object.assign(info, {
      name: v('app_name'),
      address: v('restaurant_address'),
      phone: v('restaurant_phone'),
      email: v('restaurant_email'),
      logoUrl: v('logo_url'),
      currency: v('currency') || fallbackCurrency,
      taxRate: Number(v('tax_rate')) || 0,
      serviceCharge: Number(v('service_charge')) || 0,
      footer: v('footer_text') || v('welcome_message'),
    })
  } catch { /* print with what we have */ }
  return info
}

const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
const num = value => { const n = Number(value); return Number.isFinite(n) ? n : 0 }
const DECIMALS3 = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND'])

// Turns an order from the API (or a POS sale) into receipt lines.
export function billLines(order) {
  return (order.items || []).map(item => {
    const quantity = num(item.quantity) || 1
    const unit = num(item.unit_price ?? item.price)
    const modifiers = (item.modifiers || []).map(m => ({ name: m.name, price: num(m.extra_price ?? m.price) }))
    const total = item.item_total != null ? num(item.item_total)
      : item.total_price != null ? num(item.total_price)
      : (unit + modifiers.reduce((sum, m) => sum + m.price, 0)) * quantity
    const variant = typeof item.variant === 'string' ? item.variant : item.variant?.name || item.variant_name || ''
    return { name: item.menu_item_name || item.item_name || item.name || '', variant, quantity, unit, modifiers, total, note: item.note || '' }
  })
}

// The 80mm receipt as a standalone HTML page (used for preview and print).
export function billHtml(order, business = {}, extra = {}) {
  const currency = business.currency || 'OMR'
  const digits = DECIMALS3.has(currency) ? 3 : 2
  const money = value => `${num(value).toFixed(digits)} ${esc(currency)}`
  const lines = billLines(order)
  const itemsTotal = lines.reduce((sum, l) => sum + l.total, 0)
  const tax = num(order.tax)
  const service = num(order.service_charge ?? order.serviceCharge)
  const shipping = num(order.shipping_fee ?? order.shippingFee)
  const discount = num(order.loyalty_discount ?? order.loyaltyDiscount)
  const total = order.total != null ? num(order.total) : itemsTotal + tax + service + shipping - discount
  const subtotal = order.subtotal != null ? num(order.subtotal) : itemsTotal
  const created = order.created_at ? new Date(order.created_at) : new Date()
  const dir = isRTL(getLanguage()) ? 'rtl' : 'ltr'
  const row = (label, value, strong) => `<div class="row${strong ? ' strong' : ''}"><span>${esc(label)}</span><span>${value}</span></div>`
  const paid = num(extra.amountPaid ?? order.amount_paid ?? order.amountPaid)
  const change = num(extra.change ?? order.change_amount ?? order.changeAmount)
  const method = String(order.payment_method || order.paymentMethod || '').toLowerCase()
  const methodLabel = { cash: 'Cash', card: 'Card', stripe: 'Card', bank_transfer: 'Bank transfer', loyalty: 'Loyalty points' }[method] || method

  const html = `<!doctype html><html dir="${dir}" lang="${esc(getLanguage())}"><head><meta charset="utf-8"><title>${esc(t('Bill'))} ${esc(order.order_code || order.orderCode || '')}</title>
<style>
@page { size: 80mm auto; margin: 0; }
* { box-sizing: border-box; }
body { margin: 0; padding: 4mm; width: 80mm; font: 12px/1.45 system-ui, -apple-system, 'Segoe UI', Tahoma, sans-serif; color: #000; }
.center { text-align: center; }
h1 { font-size: 16px; margin: 4px 0 2px; }
.muted { color: #444; font-size: 11px; margin: 0; }
.sep { border-top: 1px dashed #000; margin: 6px 0; }
.row { display: flex; justify-content: space-between; gap: 8px; }
.row span:last-child { white-space: nowrap; direction: ltr; }
.strong { font-weight: 700; font-size: 14px; }
.item { margin-bottom: 4px; }
.sub { padding-inline-start: 10px; font-size: 11px; color: #333; }
img { max-height: 48px; max-width: 120px; object-fit: contain; }
</style></head><body>
<div class="center">
${business.logoUrl ? `<img src="${esc(business.logoUrl)}" alt="">` : ''}
<h1>${esc(business.name || t('Bill'))}</h1>
${business.address ? `<p class="muted">${esc(business.address)}</p>` : ''}
${business.phone ? `<p class="muted" dir="ltr">${esc(business.phone)}</p>` : ''}
${business.email ? `<p class="muted">${esc(business.email)}</p>` : ''}
</div>
<div class="sep"></div>
${row(t('Order'), '#' + esc(order.order_code || order.orderCode || ''))}
${row(t('Date'), esc(created.toLocaleString(locale(), { dateStyle: 'short', timeStyle: 'short' })))}
${order.table_number || order.tableNumber ? row(t('Table'), esc(order.table_number || order.tableNumber)) : ''}
${order.customer_name ? row(t('Customer'), esc(order.customer_name)) : ''}
<div class="sep"></div>
${lines.map(l => `<div class="item">${row(`${l.quantity} × ${l.name}${l.variant ? ` (${l.variant})` : ''}`, money(l.total))}
${l.modifiers.map(m => `<div class="sub">+ ${esc(m.name)}${m.price ? ` (${money(m.price)})` : ''}</div>`).join('')}
${l.note ? `<div class="sub">${esc(t('Note:'))} ${esc(l.note)}</div>` : ''}</div>`).join('') || `<p class="muted center">${esc(t('No items'))}</p>`}
<div class="sep"></div>
${row(t('Subtotal'), money(subtotal))}
${tax ? row(business.taxRate ? `${t('Tax')} (${business.taxRate}%)` : t('Tax'), money(tax)) : ''}
${service ? row(t('Service charge'), money(service)) : ''}
${shipping ? row(t('Delivery'), money(shipping)) : ''}
${discount ? row(t('Discount'), '-' + money(discount)) : ''}
${row(t('Total'), money(total), true)}
${paid > 0 ? row(t('Paid'), money(paid)) : ''}
${change > 0 ? row(t('Change'), money(change)) : ''}
${method ? row(t('Payment'), esc(t(methodLabel))) : ''}
<div class="sep"></div>
<p class="center">${esc(business.footer || t('Thank you for your order!'))}</p>
</body></html>`
  return html
}

// Prints the receipt through a hidden frame: no pop-up to block and no
// blank window if something is missing.
export function printBill(order, business = {}, extra = {}) {
  const html = billHtml(order, business, extra)
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
  document.body.appendChild(frame)
  const doc = frame.contentDocument
  doc.open(); doc.write(html); doc.close()
  const go = () => {
    try { frame.contentWindow.focus(); frame.contentWindow.print() } finally { setTimeout(() => frame.remove(), 1000) }
  }
  // Wait for the logo so it shows on paper, but never more than a moment.
  const img = doc.querySelector('img')
  if (img && !img.complete) { let done = false; const once = () => { if (!done) { done = true; go() } }; img.onload = once; img.onerror = once; setTimeout(once, 1500) }
  else setTimeout(go, 50)
}
